import * as React from "react";
import { css } from "@emotion/react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $insertNodeToNearestRoot } from "@lexical/utils";
import {
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  LexicalCommand,
  LexicalEditor,
} from "lexical";
import { useEffect, useRef, useState } from "react";

import {
  $createLineLinkNode,
  LineLinkNode,
  LineLinkPayload,
} from "../nodes/LineLinkNode";
import { useTranslation } from "react-i18next";
import TextInput from "../ui/TextInput";
import { DialogActions } from "../ui/Dialog";
import Button from "../ui/Button";
import { LINE_ADD_FRIEND_URL } from "main/javascripts/constants/PostConstants";

export type InsertLineLinkPayload = Readonly<LineLinkPayload>;

export const INSERT_LINE_LINK_COMMAND: LexicalCommand<InsertLineLinkPayload> =
  createCommand("INSERT_LINE_LINK_COMMAND");

export function InsertLineLinkDialog({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation(["component", "button"]);

  const defaultButtonText = t("cms.buttonTextPlaceholder");
  const defaultPromotionText = t("cms.promotionTextPlaceholder");
  const [url, setUrl] = useState(LINE_ADD_FRIEND_URL);
  const [buttonText, setButtonText] = useState(defaultButtonText);
  const [promotionText, setPromotionText] = useState(defaultPromotionText);

  const hasModifier = useRef(false);

  const isDisabled = url === "" || buttonText === "";

  useEffect(() => {
    hasModifier.current = false;
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [activeEditor]);

  const onClick = (payload: InsertLineLinkPayload) => {
    activeEditor.dispatchCommand(INSERT_LINE_LINK_COMMAND, payload);
    onClose();
  };

  return (
    <div css={modalStyle}>
      <TextInput
        label={t("cms.lineUrl")}
        placeholder="https://"
        onChange={setUrl}
        value={url}
        data-test-id="image-modal-url-input"
      />
      <TextInput
        label={t("cms.buttonText")}
        placeholder={t("cms.buttonTextPlaceholder")}
        onChange={setButtonText}
        value={buttonText}
        data-test-id="image-modal-button-text-input"
      />
      <TextInput
        label={t("cms.promotionText")}
        placeholder={t("cms.promotionTextPlaceholder")}
        onChange={setPromotionText}
        value={promotionText}
        data-test-id="image-modal-promotion-text-input"
      />
      <DialogActions>
        <Button
          data-test-id="image-modal-confirm-btn"
          disabled={isDisabled}
          onClick={() => onClick({ url, buttonText, promotionText })}
        >
          {t("button:common.add")}
        </Button>
      </DialogActions>
    </div>
  );
}

export default function LineLinkPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([LineLinkNode])) {
      throw new Error("LineLinkPlugin: LineLinkNode not registered on editor");
    }

    return editor.registerCommand<InsertLineLinkPayload>(
      INSERT_LINE_LINK_COMMAND,
      (payload) => {
        const lineLinkNode = $createLineLinkNode(payload);
        $insertNodeToNearestRoot(lineLinkNode);

        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  return null;
}

const modalStyle = css`
  width: 40rem;
  max-width: 85vw;
`;
